/**
 * Step 2 · Data Upload
 * Upload collar / survey / assay / geology CSVs. Auto-detects column
 * aliases (BHID → hole_id, easting → x, etc.) via the backend.
 */
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { uploadCSV } from "@/lib/api";
import { Upload as UploadIcon, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { WizardCard, StatusRow, KV, Pill } from "@/pages/wizard/_shared";


const KINDS = [
  { key: "collar",   label: "Collar CSV",   required: true,  hint: "hole_id, x, y, z, depth" },
  { key: "survey",   label: "Survey CSV",   required: false, hint: "hole_id, depth, az, dip" },
  { key: "assay",    label: "Assay CSV",    required: true,  hint: "hole_id, from, to, grade[, element]" },
  { key: "geology",  label: "Geology CSV",  required: false, hint: "hole_id, from, to, lithology" },
];


function UploadRow({ kind, currentCount, projectId, onUploaded }) {
  const ref = useRef(null);
  const [validation, setValidation] = useState(null);
  const mut = useMutation({
    mutationFn: (file) => uploadCSV(projectId, kind.key, file),
    onSuccess: (r) => {
      setValidation(r?.validation);
      if (r?.validation?.ok) {
        toast.success(`${kind.label} — ${r.rows} rows imported`);
        onUploaded?.();
      } else {
        toast.warning(`${kind.label} — validation warnings; review below`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Upload failed"),
  });

  return (
    <div className="rounded border border-slate-200 bg-white p-3 space-y-2"
          data-testid={`w-upload-${kind.key}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="text-[13px] font-bold text-slate-800 flex items-center gap-2">
            {kind.label}
            {kind.required
              ? <Pill tone="red">REQUIRED</Pill>
              : <Pill tone="slate">Optional</Pill>}
            {currentCount > 0 && <Pill tone="green"><Check size={9} /> {currentCount.toLocaleString()} rows</Pill>}
          </div>
          <div className="text-[10px] text-slate-500 font-mono">{kind.hint}</div>
        </div>
        <input
          ref={ref}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) mut.mutate(f);
            e.target.value = ""; // allow re-pick same file
          }}
          data-testid={`w-upload-${kind.key}-file`}
        />
        <Button size="sm" variant="outline" onClick={() => ref.current?.click()}
                  disabled={mut.isPending}
                  data-testid={`w-upload-${kind.key}-btn`}>
          <UploadIcon size={12} className="mr-1" />
          {mut.isPending ? "Uploading…" : (currentCount > 0 ? "Replace" : "Choose file")}
        </Button>
      </div>
      {validation && !validation.ok && (
        <StatusRow tone="warn">
          {validation.error || "Validation warnings — review your CSV headers."}
          {validation.raw_columns && (
            <div className="text-[10px] font-mono mt-1">
              Detected columns: {validation.raw_columns.slice(0, 12).join(", ")}
            </div>
          )}
        </StatusRow>
      )}
    </div>
  );
}


export default function DataUploadStep({ projectId, stats, onDataChanged }) {
  const counts = Object.fromEntries(
    KINDS.map((k) => [k.key, stats?.[k.key]?.rows || 0])
  );
  return (
    <div className="max-w-4xl space-y-4">
      <StatusRow tone="info">
        Upload your drillhole data. SRES auto-maps industry aliases —
        BHID → hole_id, easting → x, elevation → z — so most CSVs import
        without any manual field mapping.
      </StatusRow>

      <WizardCard title="Drillhole data" testId="w-data-upload-card">
        <div className="space-y-2">
          {KINDS.map((k) => (
            <UploadRow key={k.key} kind={k} currentCount={counts[k.key]}
                        projectId={projectId} onUploaded={onDataChanged} />
          ))}
        </div>
        <div className="pt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          {KINDS.map((k) => (
            <KV key={k.key}
                k={k.label.replace(" CSV", "")}
                v={counts[k.key].toLocaleString()}
                testId={`w-upload-count-${k.key}`} />
          ))}
        </div>
      </WizardCard>
    </div>
  );
}
